import { useMemo } from "react";
import { Circle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";
import { deDateShort, deTime } from "../lib/dateFormat";

/**
 * Kleine, vertikale Timeline der Status-Übergänge einer Anfrage.
 * Erwartet ein Array `history` mit Einträgen im Backend-Format:
 *   { status, from_status, changed_at, changed_by_name, changed_by_role }
 */
export default function StatusHistoryTimeline({ history, currentStatus, className = "", testid = "status-history" }) {
  const { t } = useI18n();
  const label = (s) => (s ? (t.requests.status[s] || s) : "—");

  const rows = useMemo(() => {
    const arr = Array.isArray(history) ? history.slice() : [];
    arr.sort((a, b) => (a.changed_at || "").localeCompare(b.changed_at || ""));
    return arr;
  }, [history]);

  if (rows.length === 0) return null;

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      const dd = deDateShort(d);
      const hh = deTime(d);
      return `${dd} ${hh}`;
    } catch { return iso; }
  };

  return (
    <div className={`mt-4 pt-4 border-t border-navy/10 ${className}`} data-testid={testid}>
      <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>Verlauf</div>
      <ol className="space-y-2">
        {rows.map((e, idx) => {
          const isLast = idx === rows.length - 1;
          const isCurrent = isLast && (!currentStatus || currentStatus === e.status);
          return (
            <li
              key={`${e.changed_at}-${idx}`}
              className="relative pl-5 text-[12.5px] text-navy-dark leading-snug"
              data-testid={`status-history-row-${idx}`}
            >
              <Circle
                className="absolute left-0 top-[5px] w-2 h-2"
                strokeWidth={0}
                fill={isCurrent ? STATUS_COLORS.green : BRAND_COLORS.gold}
                aria-hidden
              />
              <div>
                <span className="text-navy-dark/60">{fmt(e.changed_at)}</span>
                <span className="text-navy-dark/30 mx-1.5">·</span>
                {e.from_status && (
                  <>
                    <span className="text-navy-dark/50">{label(e.from_status)}</span>
                    <span className="text-navy-dark/30 mx-1">→</span>
                  </>
                )}
                <span className="text-navy font-medium">{label(e.status)}</span>
                {e.changed_by_name && (
                  <>
                    <span className="text-navy-dark/30 mx-1.5">·</span>
                    <span className="text-navy-dark/70">{e.changed_by_name}</span>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
